import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const Connections = () => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newConn, setNewConn] = useState({
    name: '',
    type: 'editor_api',
    description: '',
    permissions: {
      canCreateTasks: true,
      canExecuteAgents: false,
      canReadChat: false,
      canSendMessages: false,
      sandboxed: true
    }
  });
  const [newApiKey, setNewApiKey] = useState(null);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const res = await axios.get('/api/connections');
      setConnections(res.data.connections);
    } catch (error) {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const createConnection = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post('/api/connections', newConn);
      setNewApiKey(res.data.apiKey);
      toast.success('Connection created');
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create connection');
    }
  };

  const deleteConnection = async (id) => {
    if (!window.confirm('Delete this connection? Any services using its API key will lose access.')) return;
    try {
      await axios.delete(`/api/connections/${id}`);
      toast.success('Connection deleted');
      fetchConnections();
    } catch (error) {
      toast.error('Failed to delete connection');
    }
  };

  const rotateKey = async (id) => {
    if (!window.confirm('Rotate API key? The old key will stop working immediately.')) return;
    try {
      const res = await axios.post(`/api/connections/${id}/rotate`);
      setNewApiKey(res.data.apiKey);
      toast.success('API key rotated');
    } catch (error) {
      toast.error('Failed to rotate key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Connections</h1>
        <button
          onClick={() => { setShowCreate(true); setNewApiKey(null); }}
          className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl transition-colors"
        >
          + New Connection
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-2xl p-6">
        <h3 className="text-lg font-semibold mb-2">External Integrations</h3>
        <p className="text-gray-400 text-sm">
          Connect Claude Code CLI, Windsurf editor, OpenClaw bots, or any service that needs API access.
          Each connection gets its own API key and fine-grained permissions.
        </p>
      </div>

      {/* New API Key Display */}
      {newApiKey && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-amber-400">Your New API Key</h4>
            <span className="text-xs text-amber-500">Save this - it won't be shown again!</span>
          </div>
          <div className="flex gap-2">
            <code className="flex-1 px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-sm font-mono text-amber-300 overflow-x-auto">
              {newApiKey}
            </code>
            <button
              onClick={() => copyToClipboard(newApiKey)}
              className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg transition-colors"
            >
              Copy
            </button>
          </div>
        </div>
      )}

      {/* Connections List */}
      <div className="space-y-4">
        {connections.length === 0 ? (
          <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-12 text-center">
            <p className="text-gray-500 text-lg mb-2">No connections yet</p>
            <p className="text-gray-600 text-sm">Create a connection to get an API key for external integrations</p>
          </div>
        ) : (
          connections.map((conn) => (
            <ConnectionCard
              key={conn._id}
              connection={conn}
              onDelete={() => deleteConnection(conn._id)}
              onRotate={() => rotateKey(conn._id)}
            />
          ))
        )}
      </div>

      {/* Create Connection Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">New Connection</h3>
            <form onSubmit={createConnection} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  value={newConn.name}
                  onChange={(e) => setNewConn({ ...newConn, name: e.target.value })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                  placeholder="e.g., My Claude Code CLI"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Type</label>
                <select
                  value={newConn.type}
                  onChange={(e) => setNewConn({ ...newConn, type: e.target.value })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                >
                  <option value="editor_api">Editor / CLI (Claude Code, Windsurf)</option>
                  <option value="openclaw_bot">OpenClaw Bot</option>
                  <option value="webhook">Webhook</option>
                  <option value="messaging_bridge">Messaging Bridge (Telegram, Discord)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <input
                  type="text"
                  value={newConn.description}
                  onChange={(e) => setNewConn({ ...newConn, description: e.target.value })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                  placeholder="Optional description"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Permissions</label>
                <div className="space-y-2">
                  {Object.entries(newConn.permissions).map(([key, val]) => (
                    <label key={key} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={val}
                        onChange={(e) => setNewConn({
                          ...newConn,
                          permissions: { ...newConn.permissions, [key]: e.target.checked }
                        })}
                        className="w-4 h-4 rounded bg-[#0a0a0f] border-[#2d2d3d]"
                      />
                      <span className="text-sm capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="flex-1 px-4 py-2 bg-[#2d2d3d] hover:bg-[#3d3d4d] rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 rounded-lg transition-colors"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const ConnectionCard = ({ connection, onDelete, onRotate }) => {
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState([]);

  const loadAudit = async () => {
    try {
      const res = await axios.get(`/api/connections/${connection._id}/audit`);
      setAudit(res.data.audit);
      setShowAudit(true);
    } catch {
      toast.error('Failed to load audit log');
    }
  };

  const typeIcons = {
    editor_api: '💻',
    openclaw_bot: '🤖',
    webhook: '🔗',
    messaging_bridge: '📱'
  };

  return (
    <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-2xl">
            {typeIcons[connection.type] || '🔌'}
          </div>
          <div>
            <h4 className="font-semibold">{connection.name}</h4>
            <p className="text-sm text-gray-500 capitalize">{connection.type.replace(/_/g, ' ')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${connection.isActive ? 'bg-green-500' : 'bg-gray-500'}`} />
          <span className="text-sm text-gray-400">{connection.isActive ? 'Active' : 'Inactive'}</span>
        </div>
      </div>

      {connection.description && (
        <p className="text-sm text-gray-400 mb-3">{connection.description}</p>
      )}

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-xs text-gray-500">API Key</p>
          <p className="text-sm font-mono">{connection.apiKeyPrefix}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Total Requests</p>
          <p className="text-sm font-bold">{connection.stats?.totalRequests || 0}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Last Request</p>
          <p className="text-sm">{connection.stats?.lastRequest ? new Date(connection.stats.lastRequest).toLocaleDateString() : 'Never'}</p>
        </div>
      </div>

      {/* Permissions badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(connection.permissions || {}).filter(([, v]) => v).map(([key]) => (
          <span key={key} className="px-2 py-0.5 bg-indigo-500/10 text-indigo-400 rounded text-xs">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </span>
        ))}
      </div>

      <div className="flex gap-2">
        <button onClick={onRotate} className="px-3 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-sm transition-colors">
          Rotate Key
        </button>
        <button onClick={loadAudit} className="px-3 py-2 bg-[#2d2d3d] hover:bg-[#3d3d4d] rounded-lg text-sm transition-colors">
          Audit Log
        </button>
        <button onClick={onDelete} className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm transition-colors">
          Delete
        </button>
      </div>

      {showAudit && (
        <div className="mt-4 pt-4 border-t border-[#2d2d3d]">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-sm font-medium">Audit Log</h5>
            <button onClick={() => setShowAudit(false)} className="text-xs text-gray-500 hover:text-gray-300">Close</button>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {audit.length === 0 ? (
              <p className="text-xs text-gray-500">No audit entries</p>
            ) : (
              audit.map((entry, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1">
                  <span className="text-gray-500">{new Date(entry.timestamp).toLocaleString()}</span>
                  <span className={`px-1.5 py-0.5 rounded ${getAuditBadge(entry.action)}`}>{entry.action}</span>
                  <span className="text-gray-400">{entry.details}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const getAuditBadge = (action) => {
  const badges = {
    created: 'bg-green-500/20 text-green-400',
    key_rotated: 'bg-amber-500/20 text-amber-400',
    request: 'bg-blue-500/20 text-blue-400',
    rate_limited: 'bg-red-500/20 text-red-400',
    permission_denied: 'bg-red-500/20 text-red-400',
    deactivated: 'bg-gray-500/20 text-gray-400'
  };
  return badges[action] || 'bg-gray-500/20 text-gray-400';
};

export default Connections;
